import { TPaginationParams } from '../__shared/shared.interfaces';
import { MaterialCondition } from './academicsMaterials.interfaces';

export interface IAcademicsLibraryResponse {
  items: IAcademicsLibraryItem[];
  total: number;
}

// ============================================================
// Inventory Library Borrowing API Types
// ============================================================

export interface ILibraryBorrowingRequest {
  borrowingRecord: {
    instituteId: string;
    inventoryId: string;
    borrowerId: string;
    borrowedDate: string;
    dueDate: string;
    notes?: string;
    status?: 'borrowed' | 'returned' | 'overdue' | 'lost';
    returnedDate?: string;
  };
}

export interface ILibraryBorrowingResponse {
  id: string;
  inventory: { id: string; name?: string };
  borrower: { id: string; name?: string };
  borrowedDate: string;
  dueDate: string;
  returnedDate?: string;
  status: 'borrowed' | 'returned' | 'overdue' | 'lost';
  renewalCount: number;
  fineAmount?: number;
  notes?: string;
  createdAt: string;
}

export interface ILibraryBorrowingListParams extends TPaginationParams {
  instituteId?: string;
  inventoryId?: string;
  borrowerId?: string;
  status?: string;
  overdue?: boolean;
}

// Asset types that can be borrowed from the library
export enum LibraryAssetType {
  BOOK = 'book',
  STUDY_MATERIAL = 'study_material',
  LAB_EQUIPMENT = 'lab_equipment',
  DISK = 'disk',
  AUDIO_VISUAL = 'audio_visual',
  DIGITAL_RESOURCE = 'digital_resource',
  MAGAZINE = 'magazine',
  NEWSPAPER = 'newspaper',
  REFERENCE_MATERIAL = 'reference_material',
  OTHER = 'other',
}

// Borrowing status
export enum BorrowingStatus {
  AVAILABLE = 'available',
  BORROWED = 'borrowed',
  RESERVED = 'reserved',
  OVERDUE = 'overdue',
  LOST = 'lost',
  DAMAGED = 'damaged',
  MAINTENANCE = 'maintenance',
  RETIRED = 'retired',
}

// Borrower type
export enum BorrowerType {
  STUDENT = 'student',
  STAFF = 'staff',
  PARENT = 'parent',
  VISITOR = 'visitor',
}

// Damage severity levels
export enum DamageSeverity {
  MINOR = 'minor',
  MODERATE = 'moderate',
  MAJOR = 'major',
  CRITICAL = 'critical',
}

// Damage status
export enum DamageStatus {
  REPORTED = 'reported',
  UNDER_REVIEW = 'under_review',
  REPAIRING = 'repairing',
  REPAIRED = 'repaired',
  REPLACEMENT_NEEDED = 'replacement_needed',
  WRITTEN_OFF = 'written_off',
}

// Borrowing record - tracks individual borrowing transactions
export interface IBorrowingRecord {
  recordId: string;
  assetId: string;
  borrowerId: string;
  borrowerType: BorrowerType;
  borrowerName: string;
  borrowerEmail?: string;
  borrowerPhone?: string;
  borrowedDate: string;
  dueDate: string;
  returnDate?: string;
  status: BorrowingStatus;
  renewalCount: number;
  lastRenewalDate?: string;
  fineAmount?: number;
  finePaid?: boolean;
  notes?: string;
  issuedBy: string; // Staff ID who issued the item
  returnedTo?: string; // Staff ID who received the return
  createdAt: string;
  updatedAt: string;
}

// Damage record - tracks damage incidents and repairs
export interface IDamageRecord {
  recordId: string;
  assetId: string;
  reportedDate: string;
  reportedBy: string; // User ID who reported
  reportedByName: string;
  damageSeverity: DamageSeverity;
  damageStatus: DamageStatus;
  damageDescription: string;
  damageLocation?: string; // Where on the asset the damage occurred
  images?: string[]; // URLs to damage photos
  estimatedRepairCost?: number;
  actualRepairCost?: number;
  repairStartDate?: string;
  repairCompletionDate?: string;
  repairedBy?: string; // Vendor/repair service name
  replacementAssetId?: string; // If replaced, reference to new asset
  writtenOffDate?: string;
  writtenOffReason?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// Base asset properties common to all library items
export interface IBaseLibraryAsset {
  id: string;
  assetCode: string; // Unique library asset code (e.g., LIB-BK-001)
  title: string;
  assetType: LibraryAssetType;
  description?: string;
  condition: MaterialCondition;
  currentStatus: BorrowingStatus;
  location: string; // Physical location in library (shelf, section, etc.)
  purchaseDate?: string;
  purchaseCost?: number;
  supplier?: string;
  tags: string[];
  isbn?: string; // For books
  author?: string; // For books
  publisher?: string; // For books
  edition?: string; // For books
  publicationYear?: number; // For books
  subject?: string; // Subject/category
  gradeLevel?: string; // Applicable grade levels
  language?: string;
  // Digital resource specific
  isDigital?: boolean;
  digitalFormat?: 'pdf' | 'epub' | 'mobi' | 'mp4' | 'mp3' | 'other';
  digitalUrl?: string;
  // Lab equipment specific
  equipmentType?: string;
  modelNumber?: string;
  manufacturer?: string;
  serialNumber?: string;
  // Disk/CD/DVD specific
  diskType?: 'cd' | 'dvd' | 'bluray' | 'usb' | 'external_hdd';
  diskFormat?: string;
  // Audio-visual specific
  mediaType?: 'video' | 'audio' | 'interactive';
  duration?: number; // in minutes
  // Magazine/Newspaper specific
  issueNumber?: string;
  issueDate?: string;
  periodicalName?: string;
  // Metadata
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

// Main library item interface with borrowing and damage tracking
export interface IAcademicsLibraryItem extends IBaseLibraryAsset {
  // Current borrowing information
  currentBorrowing?: IBorrowingRecord;

  // Historical records
  borrowingHistory: IBorrowingRecord[];
  damageHistory: IDamageRecord[];

  // Statistics
  totalBorrows: number;
  totalDamages: number;
  lastBorrowedDate?: string;
  lastReturnedDate?: string;

  // Availability
  isAvailable: boolean;
  canBeBorrowed: boolean; // False if damaged, lost, or retired
  maxBorrowDays: number; // Maximum days this item can be borrowed
  renewalAllowed: boolean;
  maxRenewals: number;

  // Restrictions
  borrowerRestrictions?: {
    allowedBorrowerTypes?: BorrowerType[];
    minimumGradeLevel?: string;
    maximumGradeLevel?: string;
    requiresApproval?: boolean;
  };

  // Additional metadata
  replacementCost?: number;
  notes?: string;
}
